import React, { useCallback, useMemo } from 'react';
import { useTheme } from '@mui/styles';
import { sort } from 'ramda';
import { ReferenceLine } from 'recharts';
import { PCAReportCountField, PCAReportMetric } from 'c-sdk';
import type { GridProps } from '@mui/material';
import { Grid, Typography } from '@mui/material';
import type { ChartReportPart } from 'c-reports/Types';
import { PartType, ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';
import { Translate } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import type { ByFieldChartProps } from 'c-main/Components/Campaign/CampaignReports/types';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { ByFieldOverall } from '../byFieldDataMutators';
import { sortByNameCustomField } from './sorting';

type Props = ByFieldChartProps;

// const small: GridProps = { xs: 12, lg: 6 };
const large: GridProps = { xs: 12 };
const sizes = [large, large];

const OverallCharts: React.FC<Props> = ({
    metric,
    pcaData,
    actualSelectedFieldParts,
    fieldLabel,
    expectedLabel,
    percentageLabel,
    actualLabel,
    missingLabel,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    renderTooltipLabel,
    reportWrapperProps,
    countFields,
    renderTooltipLabelHideActualValue,
    campaignId,
    field,
    filterExtras,
    hideExpected = false,
}) => {
    const theme = useTheme();
    const { dayMonthYearFormat } = useDateUtils();

    const isGroupedByCreativeName = fieldLabel === 'Creative Name';
    // Delivered only, no expected bars and no performance chart: by creative, there is no
    // expected; for a vistar_us2 campaign by a screen attribute, no target per row (UL-8204).
    const deliveredOnly = isGroupedByCreativeName || hideExpected;

    const data = useMemo(() => {
        let processedData = ByFieldOverall(
            pcaData,
            actualSelectedFieldParts,
            actualLabel,
            percentageLabel,
            expectedLabel,
            missingLabel,
            fieldLabel,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
        );
        if (isGroupedByCreativeName) {
            processedData = processedData.filter(item => item[fieldLabel] !== '');
        }

        return sortByNameCustomField(processedData, fieldLabel, field, dayMonthYearFormat);
    }, [
        pcaData,
        actualSelectedFieldParts,
        actualLabel,
        percentageLabel,
        expectedLabel,
        missingLabel,
        fieldLabel,
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        field,
        dayMonthYearFormat,
        isGroupedByCreativeName,
    ]);

    const PlaysVsExpected = useMemo<ChartReportPart[]>(() => {
        if (deliveredOnly) {
            return [
                {
                    dataKey: actualLabel,
                    barProps: { fill: theme.palette.primary.main },
                },
            ];
        }
        const bars = [] as ChartReportPart[];
        if (countFields.indexOf(PCAReportCountField.InSchedule) !== -1) {
            bars.push({
                dataKey: expectedLabel,
                barProps: { fill: theme.palette.grey['500'] },
            });
        }
        bars.push({
            dataKey: actualLabel,
            barProps: { fill: theme.palette.primary.main },
        });
        return bars;
    }, [deliveredOnly, expectedLabel, actualLabel, theme.palette, countFields]);

    const Performance = useMemo<ChartReportPart[]>(
        () => [
            {
                dataKey: percentageLabel,
                barProps: { fill: theme.palette.primary.main },
                partType: PartType.Bar,
            },
        ],
        [percentageLabel, theme.palette.primary.main],
    );

    const titles = useMemo(() => {
        if (deliveredOnly) {
            return [
                <Translate
                    key="overallActual"
                    path={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallTabTotalTabActualTitle`}
                />,
            ];
        }
        return [
            <Translate
                key="overallActual"
                path={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallCompareExpectedVsActualTitle`}
            />,
            <Translate
                key="overallPerformance"
                path={`Modules.Main.Campaigns.Overview.Reports.${metric}.overallCompareByPerformanceTitle`}
            />,
        ];
    }, [metric, deliveredOnly]);

    const RenderCharts: Props['reportWrapperProps']['ReportRender'] = useCallback(
        ({ reports }) => (
            <Grid container spacing={2} sx={{ py: 2 }}>
                {reports.map((r, ri) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Grid item key={ri} {...sizes[ri]}>
                        <Typography variant="h3" align="center">
                            {titles[ri]}
                        </Typography>
                        {r}
                    </Grid>
                ))}
            </Grid>
        ),
        [titles],
    );

    const performanceTicks = useMemo(() => {
        const highestPercentage =
            Math.ceil(
                data.reduce((highest, curr) => {
                    if (highest < Number(curr[percentageLabel])) {
                        return Number(curr[percentageLabel]);
                    }
                    return highest;
                }, 0) / 10,
            ) * 10;

        const tick = Math.ceil(highestPercentage / 5 / 10) * 10;

        let num = 0;
        const ticks = [0, 100];
        while (num < highestPercentage) {
            num += tick;
            ticks.push(num);
        }
        return sort((a, b) => a - b, ticks);
    }, [data, percentageLabel]);

    const { onResetAdvancedFilters } = useCampaignWrapperState(campaignId);

    return (
        <ReportWrapper
            onResetAdvancedFilters={onResetAdvancedFilters}
            filterExtras={filterExtras}
            reportProps={[
                {
                    type: ReportType.Bar,
                    namePrefix: `${campaignId}_${metric}_${field}_overall`,
                    xAxisDataKey: fieldLabel,
                    parts: PlaysVsExpected,
                    data,
                    renderTooltipLabel,
                    yAxisTickFormatterAsTime: metric === PCAReportMetric.Time,
                },
                ...(deliveredOnly
                    ? []
                    : [
                          {
                              type: ReportType.Bar,
                              namePrefix: `${campaignId}_${metric}_${field}_overall_performance`,
                              xAxisDataKey: fieldLabel,
                              parts: Performance,
                              data,
                              renderTooltipLabel: renderTooltipLabelHideActualValue,
                              hideLegend: true,
                              yAxisProps: {
                                  ticks: performanceTicks,
                              },
                              chartChildren: [
                                  <ReferenceLine
                                      key="ref-line"
                                      y={100}
                                      strokeDasharray="10 7"
                                      stroke="#000"
                                  />,
                              ],
                          },
                      ]),
            ]}
            {...reportWrapperProps}
            ReportRender={RenderCharts}
        />
    );
};

export default OverallCharts;
